import { Component } from '@theme/component';

/**
 * @typedef {Object} Refs
 * @property {HTMLElement} viewport
 * @property {HTMLElement} track
 * @property {HTMLElement[]} [item]
 * @property {HTMLElement} [arrows]
 * @property {HTMLElement} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLButtonElement[]} [dot]
 */

/**
 * Lightweight slideshow for arbitrary inner content.
 *
 * API:
 * - visible-items="3"
 * - show-dots="false"
 * - show-arrows="false"
 *
 * Desktop: vertical pages.
 * Mobile: horizontal, one item per page.
 *
 * @extends {Component<Refs>}
 */
export class CustomSlideshow extends Component {
  static get observedAttributes() {
    return ['visible-items', 'show-dots', 'show-arrows'];
  }

  requiredRefs = ['viewport', 'track'];

  #initialized = false;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #currentPage = 0;
  #abortController = new AbortController();
  /** @type {{ x: number, y: number } | null} */
  #pointerStart = null;

  connectedCallback() {
    if (!this.#initialized) {
      this.#build();
      this.#initialized = true;
    }

    super.connectedCallback();

    this.#bindEvents();
    this.#renderDots();
    this.#clampCurrentPage();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this.#initialized) return;

    if (name === 'show-dots' || name === 'show-arrows') {
      this.#syncControlVisibility();
      this.#renderDots();
    }

    if (name === 'visible-items') {
      this.#clampCurrentPage();
    }

    this.#update();
  }

  next() {
    this.goTo(this.#currentPage + 1);
  }

  previous() {
    this.goTo(this.#currentPage - 1);
  }

  /**
   * @param {number} page
   */
  goTo(page) {
    const clamped = Math.max(0, Math.min(page, this.pageCount - 1));
    if (clamped === this.#currentPage) return;

    this.#currentPage = clamped;
    this.#update();
  }

  get visibleItems() {
    const value = parseInt(this.getAttribute('visible-items') || '1', 10);
    return Number.isNaN(value) ? 1 : Math.max(1, value);
  }

  get showDots() {
    const value = this.getAttribute('show-dots');
    return value !== 'false';
  }

  get showArrows() {
    const value = this.getAttribute('show-arrows');
    return value !== 'false';
  }

  get isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  get itemsPerPage() {
    return this.isMobile ? 1 : this.visibleItems;
  }

  get stepSize() {
    return 1;
  }

  get items() {
    return this.refs.item || [];
  }

  get isDesktopStatic() {
    return !this.isMobile && this.items.length <= this.visibleItems;
  }

  get pageCount() {
    return Math.max(1, this.items.length - this.itemsPerPage + 1);
  }

  #build() {
    const items = Array.from(this.children).filter(
      (child) => child instanceof HTMLElement && !child.hasAttribute('data-custom-slideshow-ui')
    );

    const viewport = document.createElement('div');
    viewport.className = 'custom-slideshow__viewport';
    viewport.setAttribute('ref', 'viewport');

    const track = document.createElement('div');
    track.className = 'custom-slideshow__track';
    track.setAttribute('ref', 'track');

    for (const item of items) {
      item.classList.add('custom-slideshow__item');
      item.setAttribute('ref', 'item[]');
      track.append(item);
    }

    viewport.append(track);

    const arrows = document.createElement('div');
    arrows.className = 'custom-slideshow__arrows';
    arrows.setAttribute('data-custom-slideshow-ui', '');
    arrows.setAttribute('ref', 'arrows');

    const previous = document.createElement('button');
    previous.type = 'button';
    previous.className = 'custom-slideshow__arrow custom-slideshow__arrow--previous';
    previous.setAttribute('aria-label', 'Previous slide');
    previous.setAttribute('ref', 'previous');
    previous.innerHTML = '<span aria-hidden="true">↑</span>';

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'custom-slideshow__arrow custom-slideshow__arrow--next';
    next.setAttribute('aria-label', 'Next slide');
    next.setAttribute('ref', 'next');
    next.innerHTML = '<span aria-hidden="true">↓</span>';

    arrows.append(previous, next);

    const dots = document.createElement('div');
    dots.className = 'custom-slideshow__dots';
    dots.setAttribute('data-custom-slideshow-ui', '');
    dots.setAttribute('ref', 'dots');

    this.textContent = '';
    this.append(viewport, arrows, dots);
    this.#syncControlVisibility();
  }

  #bindEvents() {
    const { signal } = this.#abortController;

    this.refs.previous?.addEventListener('click', () => this.previous(), { signal });
    this.refs.next?.addEventListener('click', () => this.next(), { signal });
    this.refs.viewport?.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    window.addEventListener('pointerup', this.#handlePointerUp, { signal });
    window.addEventListener('pointercancel', this.#clearPointerStart, { signal });

    window.addEventListener(
      'resize',
      () => {
        this.#clampCurrentPage();
        this.#renderDots();
        this.#update();
      },
      { signal }
    );

    this.#resizeObserver?.disconnect();
    const resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver = resizeObserver;
    resizeObserver.observe(this);
    resizeObserver.observe(this.refs.track);

    for (const item of this.items) {
      resizeObserver.observe(item);
    }
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.#pointerStart = {
      x: event.clientX,
      y: event.clientY,
    };
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    if (!this.#pointerStart) return;

    const deltaX = event.clientX - this.#pointerStart.x;
    const deltaY = event.clientY - this.#pointerStart.y;
    this.#clearPointerStart();

    if (this.isMobile) {
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
      deltaX < 0 ? this.next() : this.previous();
      return;
    }

    if (Math.abs(deltaY) < 40 || Math.abs(deltaY) < Math.abs(deltaX)) return;
    deltaY < 0 ? this.next() : this.previous();
  };

  #clearPointerStart = () => {
    this.#pointerStart = null;
  };

  #syncControlVisibility() {
    const isStatic = this.isDesktopStatic;

    if (this.refs.dots instanceof HTMLElement) {
      this.refs.dots.hidden = isStatic || !this.showDots;
    }

    if (this.refs.arrows instanceof HTMLElement) {
      this.refs.arrows.hidden = isStatic || !this.showArrows;
    }
  }

  #renderDots() {
    const dotsContainer = this.querySelector('.custom-slideshow__dots');
    if (!(dotsContainer instanceof HTMLElement)) return;

    dotsContainer.replaceChildren();

    if (this.isDesktopStatic || !this.showDots) {
      this.updatedCallback();
      return;
    }

    for (let index = 0; index < this.pageCount; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'custom-slideshow__dot';
      dot.setAttribute('ref', 'dot[]');
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      dot.toggleAttribute('is-active', index === this.#currentPage);
      dot.addEventListener('click', () => this.goTo(index));
      dotsContainer.append(dot);
    }

    this.updatedCallback();
  }

  #clampCurrentPage() {
    this.#currentPage = Math.max(0, Math.min(this.#currentPage, this.pageCount - 1));
  }

  #update() {
    if (!this.items.length) return;

    this.#syncControlVisibility();

    if (this.isDesktopStatic) {
      this.refs.track.style.transform = 'none';
      this.refs.viewport.style.height = 'auto';

      this.items.forEach((item) => {
        item.toggleAttribute('is-active', true);
        item.setAttribute('aria-hidden', 'false');
      });

      this.toggleAttribute('is-mobile', this.isMobile);
      this.toggleAttribute('is-desktop-static', true);
      this.style.setProperty('--custom-slideshow-page-count', '1');
      return;
    }

    const startIndex = this.#currentPage * this.stepSize;
    const startItem = this.items[startIndex];
    if (!startItem) return;

    let offset = 0;
    let height = startItem.offsetHeight;

    if (this.isMobile) {
      offset = startItem.offsetLeft;
      height = startItem.offsetHeight;
    } else {
      const endIndex = Math.min(startIndex + this.itemsPerPage - 1, this.items.length - 1);
      const endItem = this.items[endIndex];
      if (!endItem) return;
      offset = startItem.offsetTop;
      height = endItem.offsetTop + endItem.offsetHeight - startItem.offsetTop;
    }

    this.refs.track.style.transform = this.isMobile
      ? `translate3d(${-offset}px, 0, 0)`
      : `translate3d(0, ${-offset}px, 0)`;

    this.refs.viewport.style.height = `${height}px`;

    this.items.forEach((item, index) => {
      const isVisible = index >= startIndex && index < startIndex + this.itemsPerPage;
      item.toggleAttribute('is-active', isVisible);
      item.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    });

    this.querySelectorAll('.custom-slideshow__dot').forEach((dot, index) => {
      dot.toggleAttribute('is-active', index === this.#currentPage);
    });

    if (this.refs.previous instanceof HTMLButtonElement) {
      this.refs.previous.disabled = this.#currentPage === 0;
    }

    if (this.refs.next instanceof HTMLButtonElement) {
      this.refs.next.disabled = this.#currentPage >= this.pageCount - 1;
    }

    this.toggleAttribute('is-mobile', this.isMobile);
    this.toggleAttribute('is-desktop-static', false);
    this.style.setProperty('--custom-slideshow-page-count', `${this.pageCount}`);
  }
}

if (!customElements.get('custom-slideshow')) {
  customElements.define('custom-slideshow', CustomSlideshow);
}
